import React from 'react'

import { Box, Grid, Typography } from '@mui/material'

import { ReferrerBackLink } from 'components'

import { DOCS_HEADER_HEIGHT, DOCS_MAX_WIDTH, DOCS_SLAB_TOP } from './constants'
import { DocSectionNav } from './DocSectionNav'

import type { DocEntry } from 'docs'
import type { DocsSource } from 'docs/source'

interface DocsLayoutProps {
  source: DocsSource
  /** The guide being read, when one is. Drives the nav's selected row. */
  current?: DocEntry
  /**
   * Selected nav row when the page is NOT a guide — today the glossary's sentinel. Ignored
   * when `current` is given.
   */
  activeValue?: string
  /** The document's own title, set as a document headline rather than app page chrome. */
  headline: string
  /** Right-hand rail (the table of contents). Omitted collapses the page to two columns. */
  aside?: React.ReactNode
  children: React.ReactNode
}

/**
 * The docs reader shell: the centred slab, the left index, the prose column and the optional
 * right rail.
 *
 * ## Why this exists
 *
 * Extracted when the glossary needed it. The glossary is a docs page — it is listed in the docs
 * nav, it lives under /docs, and a reader arriving from a tooltip is reading documentation — but it
 * was rendering as a standalone full-width page, so following that nav row threw away the left
 * index, the search field and the three-column measure. You left the docs to read the docs.
 *
 * The alternative was to hand-roll the slab and grid a second time inside GlossaryPage, which is
 * exactly the second hand-roll working-conventions §36 says to stop at: two copies of a layout
 * whose column widths, 72px gutter and header band have all been tuned by measurement would drift
 * on the first change to either.
 *
 * ## What stays out of here
 *
 * The scroll-spy, the lazy markdown chunk and the prev/next neighbour cards are all guide-specific
 * and remain in DocPage. This owns geometry only, which is the part both pages need to agree on.
 */
export const DocsLayout = ({
  source,
  current,
  activeValue,
  headline,
  aside,
  children,
}: DocsLayoutProps) => (
  // Capped and centred: at full width the two rails were flung to the screen edges with the prose
  // stranded between them, and the page stopped reading as a document. Inert below the max.
  <Box sx={{ maxWidth: DOCS_MAX_WIDTH, mx: 'auto', pt: DOCS_SLAB_TOP }}>
    {/* "← Back to <where you came from>", above the whole slab rather than inside a column, so all
        three columns shift together when it is present. `:empty` collapses it on a direct visit —
        ReferrerBackLink decides internally whether to render, so this wrapper cannot know. */}
    <Box sx={{ mb: 2, '&:empty': { display: 'none' } }}>
      <ReferrerBackLink />
    </Box>
    <Grid container>
      <Grid item xs={12} md={2.5} lg={2} data-tour="docs-nav">
        <DocSectionNav current={current} activeValue={activeValue} source={source} />
      </Grid>

      {/* 72px of gutter: the nav and the prose are two different documents sharing a screen, and
          the gap is what says so. */}
      <Grid
        item
        xs={12}
        md={9}
        lg={aside ? 7 : 9}
        sx={{ pl: { xs: 0, md: 18 }, pr: { xs: 0, lg: 8 } }}
      >
        {/* The title centres in the same band the left column's search field does, so the two align
            without either knowing the other's height. 32px to the first content (§40). */}
        <Box
          sx={{
            minHeight: { xs: 'auto', md: DOCS_HEADER_HEIGHT },
            display: 'flex',
            alignItems: 'center',
            mb: 8,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 28, md: 34 },
              lineHeight: 1.2,
              fontWeight: 700,
              letterSpacing: '-0.02em',
            }}
          >
            {headline}
          </Typography>
        </Box>
        {children}
      </Grid>

      {/* Three columns only where there is room for three: between md and lg the rail would squeeze
          the prose below a comfortable measure, so the page drops to two. */}
      {aside && (
        <Grid item lg={2.5} sx={{ display: { xs: 'none', lg: 'block' } }}>
          {aside}
        </Grid>
      )}
    </Grid>
  </Box>
)
